// Builds the deep-link URL to the verification cockpit for a given document.
//
// The cockpit's `verification_home` request requires several pieces of
// context (workflow_id, uploader, layout_id, filename) on top of the bare
// process_id, so we serialize everything we know into the query string. The
// cockpit then re-issues the request without having to re-query the inbox.
//
// `tableName` is critical when the document lives in a non-default
// tracking table (e.g. `config_studio_tracking` for Template Engine
// uploads). Without it the cockpit defaults to `doc_extraction_tracking`
// and `verification_home` returns no rows / no image_path.

import type { DocumentRow } from "../../types/documents";

interface BuildHrefOptions {
  /** Backend table the row lives in, e.g. "config_studio_tracking". */
  tableName?: string;
}

/**
 * Base path the verification flow lives under. Template Engine docs go
 * under `/studio/templateEngine/*` so back-navigation lands on the
 * Template Engine inbox; everything else (OCR Tracking) uses
 * `/ocr-tracking/*`. Mirrors the reference's `backPath` decision in
 * tools/ToolsView.jsx but keyed off the table name rather than the
 * current pathname.
 */
function basePathFor(opts?: BuildHrefOptions): string {
  return opts?.tableName === "config_studio_tracking"
    ? "/studio/templateEngine"
    : "/ocr-tracking";
}

function buildParams(doc: DocumentRow, opts?: BuildHrefOptions): URLSearchParams {
  const params = new URLSearchParams({ id: doc.process_id });
  if (doc.workflow_id) params.set("workflow_id", doc.workflow_id);
  if (doc.uploader) params.set("uploader", doc.uploader);
  if (doc.filename) params.set("filename", doc.filename);
  if (doc.layout_id) params.set("layout_id", doc.layout_id);
  if (opts?.tableName) params.set("table_name", opts.tableName);
  return params;
}

export function buildVerifyHref(doc: DocumentRow, opts?: BuildHrefOptions): string {
  return `${basePathFor(opts)}/verify?${buildParams(doc, opts).toString()}`;
}

/**
 * Deep-link to the Business Rules page for the same document. Uses the
 * same query-string shape as `buildVerifyHref` so BusinessRulesPage can
 * read process_id / workflow_id / uploader / filename / layout_id without
 * re-querying the inbox.
 */
export function buildBusinessRulesHref(
  doc: DocumentRow,
  opts?: BuildHrefOptions,
): string {
  return `${basePathFor(opts)}/business-rules?${buildParams(doc, opts).toString()}`;
}

/**
 * Deep-link to the Final Checker — Business Rule (BR) verification
 * page. Mirrors ocr-ui's `Verification` view (different from its
 * `ToolsView`): a slim review UI with image on the left, line/field
 * tabs on the right, and Reset/Save/Clear/Reject/Submit at the top.
 *
 * Routes to a separate page (`final-checker` segment) rather than the
 * TV cockpit so BR reviewers don't see Tools View configuration,
 * manual adjustment, rule editor, etc. — they have exactly one job:
 * review the BR pipeline's output and approve/reject.
 */
export function buildFinalCheckerHref(
  doc: DocumentRow,
  opts?: BuildHrefOptions,
): string {
  return `${basePathFor(opts)}/final-checker?${buildParams(doc, opts).toString()}`;
}
